import type { FindReplaceRule, Selector } from '@carboncopyco/types';
import type { IFindAndReplaceRuleType } from '@carboncopyco/types';
import { LoadingDots } from '@carboncopyco/ui';
import type React from 'react';
import { useEffect, useState } from 'react';
import { type TFindAndReplaceRuleStatus, ruleToControls } from './types';

export interface FindAndReplaceRuleFormProps {
  type: IFindAndReplaceRuleType;
  rule?: FindReplaceRule;
  rules?: FindReplaceRule[];
  onSubmit: (rule: FindReplaceRule, selectors?: Selector[]) => Promise<unknown>;
  onSuccess?: () => void;
  onError?: () => void;
  onCancel?: () => void;
  autoFocus?: boolean;
  onFocus?: (e: React.FocusEvent<HTMLElement>) => void;
}

export const FindAndReplaceRuleForm = ({
  type,
  rule,
  rules,
  onSubmit,
  onSuccess,
  onError,
  onCancel,
  autoFocus,
  onFocus,
}: FindAndReplaceRuleFormProps) => {
  const [status, setStatus] = useState<TFindAndReplaceRuleStatus>('idle');
  const [error, setError] = useState<string>();

  const submitForm = async (
    value: Partial<FindReplaceRule>,
    selectors?: Selector[]
  ) => {
    const updatedRule = {
      ...(rule ?? {}),
      ...value,
    } as FindReplaceRule;

    try {
      setStatus('submitting');
      await onSubmit(updatedRule, selectors);
      setStatus('success');
      onSuccess?.();
    } catch (e) {
      // Prevent from error status from firing twice
      console.error(e);
      setStatus('error');
      setError(e.message);
      onError?.();
      throw e;
    }
  };

  useEffect(() => {
    setStatus('idle');
  }, [type]);

  const ToolControls = ruleToControls[type];

  return (
    <div className="relative">
      <div className={status === 'submitting' ? 'opacity-0' : undefined}>
        <ToolControls
          onSubmit={submitForm}
          submitting={status === 'submitting'}
          error={error}
          rule={rule}
          rules={rules}
          onCancel={onCancel}
          autoFocus={autoFocus}
          onFocus={onFocus}
        />
      </div>
      {status === 'submitting' && (
        <div className="absolute top-0 left-0 w-full h-full flex flex-col justify-center items-center">
          <LoadingDots />
        </div>
      )}
    </div>
  );
};
